import type { TFeatureFlags } from "@citrine/configuration";
import { useConfiguration } from "@citrine/resources/configuration";

interface FeatureFlagValue<FeatureType extends keyof TFeatureFlags> {
  feature: FeatureType;
  value?: TFeatureFlags[FeatureType];
}

type FeatureFlagProps<FeatureType extends keyof TFeatureFlags> = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
} & (
  | {
      feature?: undefined;
      value?: undefined;
      test: (config: TFeatureFlags) => boolean;
    }
  | (FeatureFlagValue<FeatureType> & { test?: undefined })
);

/**
 * Use a feature flag to determine whether to display a new feature or a fallback
 *
 * This component can be passed either a feature flag or a test function to
 * operate on multiple configuration values
 *
 * Examples:
 *  <FeatureFlag flag="has_triton_access" />
 *  <FeatureFlag flag="has_triton_access" value={false} />
 *  <FeatureFlag test={({ has_triton_access }) => has_triton_access} />
 */
export function FeatureFlag<FeatureType extends keyof TFeatureFlags>({
  children,
  fallback = null,
  feature,
  value,
  test,
}: FeatureFlagProps<FeatureType>) {
  const testValue = value ?? true;
  const config = useConfiguration();
  const show = test?.(config) ?? config[feature!] === testValue;
  return <>{show ? children : fallback}</>;
}
